import { Controller } from "@hotwired/stimulus"
import SlimSelect from "slim-select"

// Fallbacks for the server-side copy passed in through the `translations`
// value; a key the view did not send never reads as `undefined`.
const DEFAULT_TRANSLATIONS = {
  searchPlaceholder: "Search...",
  searchText: "No results found",
  // Read out by screen readers in front of a chip's name ("Remove Alena")
  removeText: "Remove"
}

export default class extends Controller {
  static targets = ["select"]
  static values = {
    translations: { type: Object, default: {} },
    placeholder: { type: String, default: "" },
    showSearch: { type: Boolean, default: true },
    // A multi-select that keeps the list open between picks turns this off
    closeOnSelect: { type: Boolean, default: true },
    searchHighlight: { type: Boolean, default: false },
    maxLines: { type: Number, default: 5 }
  }

  static OPTION_HEIGHT_REM = 4.8
  static SEARCH_HEIGHT_REM = 5.6

  get #texts() {
    return { ...DEFAULT_TRANSLATIONS, ...this.translationsValue }
  }

  connect() {
    this.element.classList.add("ss-lucanto")
    // The field grows with its selected values instead of clipping them
    this.element.classList.toggle("ss-lucanto--multiple", this.selectTarget.multiple)

    this.contentEl = document.createElement("div")
    this.contentEl.classList.add("ss-lucanto__content-wrapper")
    this.element.appendChild(this.contentEl)

    this.slimSelect = new SlimSelect({
      select: this.selectTarget,
      settings: {
        placeholderText: this.placeholderValue,
        searchPlaceholder: this.#texts.searchPlaceholder,
        searchText: this.#texts.searchText,
        showSearch: this.showSearchValue,
        closeOnSelect: this.closeOnSelectValue,
        searchHighlight: this.searchHighlightValue,
        removeText: this.#texts.removeText,
        contentLocation: this.contentEl,
        contentPosition: "relative",
        // slim-select 4 defaults to a modal on phones; the list is laid out inside contentEl here
        modal: "off"
      }
    })

    const height = (this.maxLinesValue + 0.5) * this.constructor.OPTION_HEIGHT_REM + this.constructor.SEARCH_HEIGHT_REM
    this.contentEl.style.setProperty("--ss-content-height", `${height}rem`)

    if (this.showSearchValue) {
      this.#injectSearchIcon()
    }
  }

  #injectSearchIcon() {
    const searchContainer = this.contentEl.querySelector(".ss-search")
    if (!searchContainer) return

    searchContainer.style.position = "relative"

    const icon = document.createElement("span")
    icon.classList.add("material-symbols-outlined", "ss-lucanto__search-icon")
    icon.textContent = "search"
    searchContainer.appendChild(icon)
  }

  disconnect() {
    this.slimSelect?.destroy()
    this.contentEl?.remove()
    this.element.classList.remove("ss-lucanto", "ss-lucanto--multiple")
  }
}
